import { useEffect, useRef, useState } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader } from '../../components/common/PageHeader';
import { TeamsManager } from '../../components/teams/TeamsManager';
import {
  useAdminGetOrganization,
  useAdminUpdateOrganization,
  useAdminDeleteOrganization,
  useListModels,
  useUploadOrganizationLogo,
  useRemoveOrganizationLogo,
  getAdminListOrganizationsQueryKey,
  getAdminGetOrganizationQueryKey,
} from '../../api/generated/generated';
import type { ModelConfig } from '../../api/generated';
import { getErrorMessage } from '../../api/client';

export function AdminOrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: orgResp, isLoading, error } = useAdminGetOrganization(orgId ?? '', {
    query: { enabled: !!orgId },
  });
  const org = orgResp?.status === 200 ? orgResp.data : undefined;
  const updateOrg = useAdminUpdateOrganization({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getAdminListOrganizationsQueryKey() });
        if (orgId) {
          queryClient.invalidateQueries({ queryKey: getAdminGetOrganizationQueryKey(orgId) });
        }
      },
    },
  });
  const deleteOrg = useAdminDeleteOrganization({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getAdminListOrganizationsQueryKey() });
      },
    },
  });
  const { data: models } = useListModels({
    query: {
      select: (response): ModelConfig[] => (response.status === 200 ? response.data : []),
    },
  });
  const invalidateOrg = () => {
    if (orgId) {
      queryClient.invalidateQueries({ queryKey: getAdminGetOrganizationQueryKey(orgId) });
    }
  };
  const uploadLogo = useUploadOrganizationLogo({ mutation: { onSuccess: invalidateOrg } });
  const removeLogo = useRemoveOrganizationLogo({ mutation: { onSuccess: invalidateOrg } });
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [description, setDescription] = useState('');
  const [valueProposition, setValueProposition] = useState('');
  const [messagingPoints, setMessagingPoints] = useState<string[]>([]);
  const [newPoint, setNewPoint] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Seed form state once per loaded organization; avoid clobbering in-flight edits
  // when the query refetches after a successful save.
  useEffect(() => {
    if (org) {
      setName(org.name);
      setSelectedModel(org.selected_model ?? '');
      setDescription(org.description ?? '');
      setValueProposition(org.value_proposition ?? '');
      setMessagingPoints(org.messaging_points ?? []);
    }
  }, [org?.id]);

  if (!orgId) {
    return <Navigate to="/admin/organizations" replace />;
  }

  if (!user?.superAdmin) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        You do not have permission to access this page.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !org) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load organization.
      </Alert>
    );
  }

  const handleAddPoint = () => {
    const trimmed = newPoint.trim();
    if (!trimmed || trimmed.length > 200 || messagingPoints.length >= 10) return;
    setMessagingPoints([...messagingPoints, trimmed]);
    setNewPoint('');
  };

  const handleRemovePoint = (index: number) => {
    setMessagingPoints(messagingPoints.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const updates: {
      name?: string;
      selected_model?: string;
      description?: string;
      value_proposition?: string;
      messaging_points?: string[];
    } = {};
    if (name !== org.name) updates.name = name;
    if (selectedModel !== (org.selected_model ?? '')) updates.selected_model = selectedModel;
    if (description !== (org.description ?? '')) updates.description = description;
    if (valueProposition !== (org.value_proposition ?? ''))
      updates.value_proposition = valueProposition;
    if (JSON.stringify(messagingPoints) !== JSON.stringify(org.messaging_points ?? []))
      updates.messaging_points = messagingPoints;

    if (Object.keys(updates).length === 0) {
      setSnackbar({ open: true, message: 'No changes to save.', severity: 'success' });
      return;
    }
    try {
      await updateOrg.mutateAsync({ orgId: org.id, data: updates });
      setSnackbar({ open: true, message: 'Organization updated.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: getErrorMessage(err), severity: 'error' });
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadLogo.mutateAsync({ orgId: org.id, data: { file } });
      setSnackbar({ open: true, message: 'Logo uploaded.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: getErrorMessage(err), severity: 'error' });
    }
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleLogoRemove = async () => {
    try {
      await removeLogo.mutateAsync({ orgId: org.id });
      setSnackbar({ open: true, message: 'Logo removed.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: getErrorMessage(err), severity: 'error' });
    }
  };

  const isDirty =
    name !== org.name ||
    selectedModel !== (org.selected_model ?? '') ||
    description !== (org.description ?? '') ||
    valueProposition !== (org.value_proposition ?? '') ||
    JSON.stringify(messagingPoints) !== JSON.stringify(org.messaging_points ?? []);

  const handleDelete = async () => {
    try {
      await deleteOrg.mutateAsync({ orgId: org.id });
      navigate('/admin/organizations');
    } catch (err) {
      setSnackbar({ open: true, message: getErrorMessage(err), severity: 'error' });
      setDeleteDialogOpen(false);
    }
  };

  return (
    <Box>
      <PageHeader title={org.name} />
      <Grid container spacing={3}>
        {/* Org Info / Settings */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
                <TextField
                  label="Organization Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  size="small"
                  fullWidth
                  slotProps={{
                    htmlInput: { minLength: 3, maxLength: 100 }
                  }}
                />

                <FormControl fullWidth size="small">
                  <InputLabel>AI Model</InputLabel>
                  <Select
                    value={selectedModel}
                    label="AI Model"
                    onChange={(e) => setSelectedModel(e.target.value)}
                  >
                    {models?.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name} {model.default ? '(Default)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider />
                <Typography variant="subtitle2">Company Profile</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  label="Company Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  helperText={`${description.length}/1000`}
                  slotProps={{
                    htmlInput: { maxLength: 1000 }
                  }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  label="Value Proposition"
                  value={valueProposition}
                  onChange={(e) => setValueProposition(e.target.value)}
                  helperText={`${valueProposition.length}/1000`}
                  slotProps={{
                    htmlInput: { maxLength: 1000 }
                  }}
                />

                <Typography variant="body2">Key Messaging Points</Typography>
                {messagingPoints.length < 10 && (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a messaging point and press Enter"
                    value={newPoint}
                    onChange={(e) => setNewPoint(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPoint();
                      }
                    }}
                    helperText={`${messagingPoints.length}/10 points`}
                    slotProps={{
                      htmlInput: { maxLength: 200 }
                    }}
                  />
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {messagingPoints.map((point, i) => (
                    <Chip key={i} label={point} size="small" onDelete={() => handleRemovePoint(i)} />
                  ))}
                </Box>

                <Divider />
                <Typography variant="subtitle2">Branding</Typography>
                <Stack direction="row" spacing={2} sx={{
                  alignItems: "center"
                }}>
                  <Box
                    sx={{
                      width: 160,
                      height: 64,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'background.default',
                      overflow: 'hidden',
                    }}
                  >
                    {org.logo_url ? (
                      <Box
                        component="img"
                        src={org.logo_url}
                        alt={org.name}
                        sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{
                        color: "text.secondary"
                      }}>
                        No logo
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      ref={logoInputRef}
                      onChange={handleLogoUpload}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<PhotoCameraIcon />}
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadLogo.isPending}
                      size="small"
                    >
                      {uploadLogo.isPending ? 'Uploading...' : org.logo_url ? 'Replace' : 'Upload'}
                    </Button>
                    {org.logo_url && (
                      <IconButton
                        onClick={handleLogoRemove}
                        disabled={removeLogo.isPending}
                        size="small"
                        sx={{ ml: 1 }}
                        aria-label="Remove logo"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Stack>

                <Divider />
                <Typography variant="caption" sx={{
                  color: "text.secondary"
                }}>
                  Created by {org.created_by} · {new Date(org.created_at).toLocaleString()} · Updated{' '}
                  {new Date(org.updated_at).toLocaleString()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={updateOrg.isPending || !isDirty}
                  >
                    {updateOrg.isPending ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Stats */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistics
              </Typography>
              <Typography>Members: {org.member_count}</Typography>
              <Typography>Conversations: {org.conversation_count}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Members Table */}
        <Grid size={12}>
          <Paper>
            <Typography variant="h6" sx={{ p: 2 }}>
              Members ({org.members.length})
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {org.members.map((member) => (
                    <TableRow key={member.uid}>
                      <TableCell>{member.display_name}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={member.role}
                          size="small"
                          color={member.role === 'org_owner' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(member.joined_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Teams */}
        <Grid size={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Teams
            </Typography>
            <TeamsManager orgId={org.id} canManage />
          </Paper>
        </Grid>
      </Grid>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{org.name}&quot;? This will permanently remove
            all members, conversations, prompts, and related data. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteOrg.isPending}
          >
            {deleteOrg.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
